import * as React from 'react'

import {
  PageMeta,
  HowToUse,
  BehindScenes,
  Callout,
  Flow,
  KVTable,
} from '@/components/docs/primitives'
import { H3, P, UL } from '@/components/docs/prose'
import { brand } from '@/brand/brand'

export function DocAssetLibrary() {
  return (
    <>
      <PageMeta
        title="Saved Context"
        summary={`Every image and video you save in ${brand.productName} — product shots, lifestyle photography, generated avatars, mood boards, reference videos — organized into folders and auto-described by AI so you can find anything by content, not just filename.`}
      />

      <HowToUse>
        <H3>Folder types</H3>
        <P>
          Every asset in {brand.productName} lives inside a folder. There are four kinds, each
          with a distinct purpose:
        </P>
        <UL>
          <li>
            <strong>SKU folders</strong> — one per product, auto-created when you add
            a product to the Products library. Holds all photography specific to that product:
            the Novera Body Oil hero shot, its 3/4 angle, its label close-up. The Radiance
            folder holds the serum's amber dropper shots. Each SKU folder stays clean and
            separate.
          </li>
          <li>
            <strong>General folders</strong> — for shared brand assets that apply across all
            products. Mood boards, botanical still-lifes, the gold-and-warm-neutral palette
            reference, marble texture shots, anything that sets your brand's visual world but
            isn't tied to a single SKU.
          </li>
          <li>
            <strong>Style Reference folders</strong> — store videos whose camera work,
            lighting, or pacing you want future ads to mimic. A slow dolly over a vanity
            table, a golden-hour tracking shot, a close-up pour — if you want the AI to
            produce that feel, the reference video goes here.
          </li>
          <li>
            <strong>Context folders</strong> — inspiration, archive material, reference ads,
            anything that doesn't belong in the other three types.
          </li>
        </UL>
        <Callout variant="tip">
          Don't reflexively dump everything into General. Organise by intent — your brand's mood
          boards go in General, Radiance label shots go in the Radiance SKU folder, reference
          videos you want the AI to mimic go in Style Reference. It's the difference between
          an assistant that can find and use things and one that can't.
        </Callout>

        <H3>Uploading</H3>
        <P>
          Open the Asset Library and click Add Asset. Drop a file onto the dialog or click to
          browse. Supported formats are JPG, PNG, and WEBP for images, and MP4 and MOV for
          video. The maximum file size is 500MB per file. Choose a destination folder on the
          way in — if you don't, the file lands in a catch-all Uploads folder you can
          reorganise later.
        </P>

        <H3>Finding things</H3>
        <P>
          Filter by type, folder, analysis status, or free-text search. The search runs against
          the AI description, not just the filename — so typing "rose petals on marble" finds
          the right lifestyle shot even if the filename is IMG_2847.jpg. "Amber glass, golden
          hour" finds the Novera Body Oil hero even if it was never renamed. The more descriptive
          the AI's analysis, the better the search.
        </P>

        <H3>Using assets elsewhere</H3>
        <P>
          Any asset can be picked as a reference in Create Ad or Create Image. Products link to
          assets with roles. Nothing is locked to one folder — a lifestyle shot that lives in
          General can also be referenced in a Radiance ad without moving it.
        </P>

        <H3>Analysis status</H3>
        <P>
          Every uploaded asset goes through an AI description pass. Three states are possible:
        </P>
        <UL>
          <li>
            <strong>Pending</strong> — the AI hasn't described it yet. You can use the asset
            as a reference right away; the description and tags just catch up in the background.
          </li>
          <li>
            <strong>Completed</strong> — described, tagged, and fully searchable by content.
          </li>
          <li>
            <strong>Failed</strong> — the AI couldn't describe it. Usually an oversized file
            or a corrupt upload. Retry from the asset's detail view. The asset still works as
            a visual reference in any generation — it just won't show up in free-text searches
            until the description is written.
          </li>
        </UL>
      </HowToUse>

      <BehindScenes>
        <H3>What happens when you upload</H3>
        <Flow
          steps={[
            'You drop a file into the Asset Library',
            'The file is saved',
            'Gemini looks at it and writes a one-paragraph description in plain English',
            'It also picks out short tags, dominant colors, and classifies the scene type — product, model, lifestyle, reference, or context',
            'Status flips from Pending to Completed',
          ]}
        />

        <H3>What gets stored about each asset</H3>
        <KVTable
          rows={[
            {
              key: 'Description',
              value:
                'A sentence in plain English: "Amber glass bottle on white marble with scattered rose petals, warm side lighting"',
            },
            {
              key: 'Tags',
              value:
                'Short keywords: "marble, amber glass, rose petals, overhead, warm tones, botanical"',
            },
            {
              key: 'Dominant colors',
              value:
                'Top 3–5 colors extracted from the image — useful for spotting anything that breaks the gold-neutral-white brand palette',
            },
            {
              key: 'Scene type',
              value:
                'model (person), product, lifestyle, reference, or context — controls which pickers the asset appears in across Create Ad and Create Image',
            },
            {
              key: 'Visual characteristics',
              value:
                'Notes on lighting quality, composition, and camera angle — helps the AI understand the mood of the asset before using it as a reference',
            },
          ]}
        />
        <Callout variant="tip">
          If Gemini misclassifies an asset's scene type — for example labelling a Radiance
          product shot as "lifestyle" — you can correct it from the detail modal. The correct
          scene type ensures the asset surfaces in the right pickers across Create Ad and
          Create Image.
        </Callout>

        <H3>Why the AI description matters</H3>
        <P>
          The description is what makes free-text search work across hundreds of assets without
          needing precise filenames. It is also what lets the rest of the app recommend relevant
          assets when you are picking references — the AI reads descriptions to judge which
          images match the scene you are setting up.
        </P>
      </BehindScenes>
    </>
  )
}
